/**
 * Voluum-style flow name: country label, name, preview, workspace.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import type { VoluumFlowUpdate } from '@trafficpulse/shared';

import { Input } from '@/components/ui/form/input/Input';

import FlowMetaFieldLabel from '../FlowMetaFieldLabel';
import {
  composeName,
  countryEntry,
  parseBaseName,
  primaryCountry,
  workspaceLabel,
} from './utils';

export interface FlowNameFieldsProps {
  draft: VoluumFlowUpdate;
  onPatch: (mutator: (d: VoluumFlowUpdate) => void) => void;
}

const FlowNameFields: FC<FlowNameFieldsProps> = ({ draft, onPatch }) => {
  const primary = primaryCountry(draft);
  const baseName = parseBaseName(draft.name, primary?.display);
  const preview = composeName(primary?.display, baseName);

  const onCountrySelect = (code: string) => {
    onPatch((d) => {
      const list = d.countries ?? [];
      const picked = list.find((c) => c.code === code);
      if (!picked) return;
      const oldP = primaryCountry(d);
      const base = parseBaseName(d.name, oldP?.display);
      const rest = list.filter((c) => c.code !== code);
      d.countries = [picked, ...rest];
      const newP = primaryCountry(d);
      d.name = composeName(newP?.display, base);
    });
  };

  return (
    <>
      <FlowMetaFieldLabel
        label="Country label"
        hint="First country on this flow is used like Voluum’s country tag in the displayed flow name."
      >
        {(draft.countries ?? []).length === 0 ? (
          <p className="text-[11px] leading-snug text-muted-foreground">
            + label.
          </p>
        ) : (
          <select
            value={primary?.code ?? ''}
            onChange={(e) => onCountrySelect(e.target.value)}
            className="h-8 w-full max-w-md rounded-md border border-input bg-background px-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
            aria-label="Primary country for flow name"
          >
            {(draft.countries ?? []).map((c) => (
              <option key={c.code} value={c.code}>
                {countryEntry(c as { code: string } & Record<string, unknown>).display}
              </option>
            ))}
          </select>
        )}
      </FlowMetaFieldLabel>

      <FlowMetaFieldLabel label="Name">
        {(draft.countries ?? []).length === 0 ? (
          <Input
            value={draft.name}
            onChange={(e) =>
              onPatch((d) => {
                d.name = e.target.value;
              })
            }
            placeholder="Flow name"
            aria-label="Flow name"
          />
        ) : (
          <Input
            value={baseName}
            onChange={(e) =>
              onPatch((d) => {
                d.name = composeName(primaryCountry(d)?.display, e.target.value);
              })
            }
            placeholder="Name after country label"
            aria-label="Flow name suffix"
          />
        )}
      </FlowMetaFieldLabel>

      <p className="text-[11px] leading-snug text-muted-foreground">
        Preview:{' '}
        <span className="font-semibold text-foreground">{preview}</span>
      </p>

      <FlowMetaFieldLabel
        label="Workspace"
        hint="Workspace membership is sent to Voluum as an id."
      >
        <div className="rounded-md border border-border/70 bg-muted/15 px-2.5 py-1.5 text-xs text-foreground">
          {workspaceLabel(draft.workspace)}
        </div>
      </FlowMetaFieldLabel>
    </>
  );
};

export default FlowNameFields;
